import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Editor, Toolbar } from 'ngx-editor';
import { richTextToHtml } from 'src/app/leader-toolkit/utils/rich-text.util';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';

export const RECURRING_OPTIONS = [
  { value: 'Monthly',   label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Annually',  label: 'Annually' },
];

@Component({
  selector: 'vc-create-new-task',
  standalone: false,
  templateUrl: './create-new-task.component.html',
  styleUrl: './create-new-task.component.scss',
})
export class CreateNewTaskComponent implements OnInit, OnDestroy {
  taskForm: FormGroup;
  categoryList: any[] = [];
  hospitalList: any[] = [];
  quarterList: any[] = [];
  goalList: any[] = [];
  recurringOptions = RECURRING_OPTIONS;
  userList: any[] = [];

  isAdmin = false;
  currentUser: any = null;
  loading = false;
  formSubmitted = false;
  minDate = new Date();
  minEndDate = new Date();
  upcomingOccurrences: any[] = [];
  showOccurrences = false;
  assignToSuggestions: any[] = [];

  // ─── assign-to-all (RM/DO/VP) ───────────────────────────────────────────────
  readonly roleOptions = ['RM', 'DO', 'VP'];
  roleAssignees: any[] = []; // flat: { practiceSKey, role, assignee: { id, displayName, email } | null }
  roleAssigneesLoading = false;

  // ─── hospital search ──────────────────────────────────────────────────────
  hospitalSearch = '';
  @ViewChild('hospitalSearchInput') hospitalSearchInput!: ElementRef<HTMLInputElement>;

  get filteredHospitalList(): any[] {
    if (!this.hospitalSearch) return this.hospitalList;
    const q = this.hospitalSearch.toLowerCase();
    return this.hospitalList.filter(h => (h.practiceName || '').toLowerCase().includes(q));
  }

  isHospitalVisible(h: any): boolean {
    if (!this.hospitalSearch) return true;
    return (h.practiceName || '').toLowerCase().includes(this.hospitalSearch.toLowerCase());
  }

  focusHospitalSearch() {
    setTimeout(() => this.hospitalSearchInput?.nativeElement?.focus(), 50);
  }

  // ─── assignee search ──────────────────────────────────────────────────────
  assigneeQuery = '';
  assigneeSuggestions: any[] = [];
  selectedAssignees: any[] = []; // multiple for multi-hospital bulk tasks; capped to 1 for a single hospital
  showAssigneeDrop = false;
  private assigneeTimer: any;

  // ─── description rich text editor ──────────────────────────────────────────
  descriptionEditor: Editor = new Editor();
  descriptionToolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h2', 'h3', 'h4'] }],
    ['link'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  isEditMode: boolean = false;
  taskId: any;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateNewTaskComponent>,
    public leaderService: LeaderService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadStaticData();
  }

  ngOnDestroy(): void {
    this.descriptionEditor.destroy();
  }

  // ─── form setup ──────────────────────────────────────────────────────────────

  private buildForm() {
    this.taskForm = this.fb.group({
      task:            [null, Validators.required],
      category:        [null, Validators.required],
      hospitalIds:     [[], ],           // multi-select; no hard required so "no hospital" is allowed
      quarter:         [null],
      affiliatedGoal:  [0],
      // Starts as '' rather than null on purpose: ngx-editor only switches its
      // output to HTML when the initial value is a *string* (see isHtml() in the
      // library). With null it emits its document object instead, which is not
      // what the task API expects. This is the same reason the comment editors
      // initialise their bound string to ''.
      taskDescription: [''],
      dueDate:         [null, Validators.required],
      status:          [11],
      attendeesIds:    [[], Validators.required],   // array of assignee ids
      assignToRoles:   [[]],           // "Assign to all: RMs/DOs/VPs" — only offered for multi-hospital
      isCritical:      [false],
      recurringInterval: ['One-time'],
      endDate:         [null],
      dueDaysFromStartDate: [0],
    });

    this.taskForm.get('hospitalIds')!.valueChanges.subscribe(() => {
      this.onHospitalsChange();
      this.updateAssigneeValidity();
    });
    this.taskForm.get('quarter')!.valueChanges.subscribe(() => this.tryLoadGoals());
    this.taskForm.get('recurringInterval')!.valueChanges.subscribe(() => {
      this.onRecurringChange();
      this.generateOccurrences();
    });
    this.taskForm.get('dueDate')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.taskForm.get('endDate')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.taskForm.get('dueDaysFromStartDate')!.valueChanges.subscribe(() => this.generateOccurrences());
    this.updateAssigneeValidity();
  }

  // ─── data loading ─────────────────────────────────────────────────────────

  private loadStaticData() {
    this.leaderService.getCurrentUserInfo().subscribe((info: any) => {
      this.isAdmin = !!info?.isAdmin;
      this.currentUser = info;
    });

    this.leaderService.getHospitalList().subscribe((res: any) => {
      this.hospitalList = res.data || [];
      this.applyDialogPrefill();
    });

    this.leaderService.getTaskLookUpData().subscribe((res: any) => {
      this.categoryList = res.data?.TaskCategory || [];
    });

    this.leaderService.getQtrList().subscribe((res: any) => {
      this.quarterList = res.data || [];
      this.applyDialogPrefill();
    });
  }

  // ─── hospital selection ───────────────────────────────────────────────────

  get selectedHospitalIds(): number[] {
    return this.taskForm.get('hospitalIds')!.value || [];
  }

  get isMultiHospital(): boolean {
    return this.selectedHospitalIds.length > 1;
  }

  /** All the new bulk-assignment UI (goal coverage, assign-to-all roles, multi-assignee chips,
   *  fan-out preview) applies only to a NEW multi-hospital task — updatetask isn't built to take
   *  a bulk payload, so editing an existing task must behave exactly as it did before this feature,
   *  even if its Hospital field happens to have more than one selected. */
  get isBulkMode(): boolean {
    return this.isMultiHospital && !this.isEditMode;
  }

  get goalQuarterDisabled(): boolean {
    // Multi-hospital no longer locks Quarter/Goal for a NEW task — goals are shown as the union
    // across selected hospitals (see tryLoadGoals). But editing keeps the original lock: the
    // update API only ever applies to one hospital, so multi-hospital + edit must stay locked out.
    return this.isRecurring || (this.isMultiHospital && this.isEditMode);
  }

  toggleSelectAllHospitals() {
    const allIds = this.hospitalList.map(h => h.practiceSKey);
    const current: number[] = this.selectedHospitalIds;
    const allSelected = allIds.every(id => current.includes(id));
    this.taskForm.get('hospitalIds')!.setValue(allSelected ? [] : allIds);
  }

  get allHospitalsSelected(): boolean {
    const allIds = this.hospitalList.map(h => h.practiceSKey);
    return allIds.length > 0 && allIds.every(id => this.selectedHospitalIds.includes(id));
  }

  get someHospitalsSelected(): boolean {
    return this.selectedHospitalIds.length > 0 && !this.allHospitalsSelected;
  }

  onHospitalsChange() {
    // 'affiliatedGoal' means different things for single vs. multi hospital (a concrete
    // goalTargetId vs. a goalDefinitionId) — always re-pick it when the selection changes.
    this.taskForm.get('affiliatedGoal')!.setValue(0);
    if (this.goalQuarterDisabled) {
      this.taskForm.get('quarter')!.setValue(null);
      this.goalList = [];
    } else {
      this.tryLoadGoals();
    }
    if (!this.isBulkMode) {
      this.taskForm.get('assignToRoles')!.setValue([]);
      // Single-hospital (or editing) tasks only take one assignee — keep at most the first if we
      // just dropped out of bulk mode with several chips selected.
      if (this.selectedAssignees.length > 1) {
        this.selectedAssignees = [this.selectedAssignees[0]];
        this.taskForm.get('attendeesIds')!.setValue(this.selectedAssignees.map(a => a.id));
      }
    }
    this.refreshRoleAssignees();
  }

  hospitalDisplayValue(): string {
    const ids: number[] = this.selectedHospitalIds;
    if (ids.length === 0) return '';
    if (ids.length === this.hospitalList.length) return 'All Hospitals';
    if (ids.length === 1) {
      const h = this.hospitalList.find(x => x.practiceSKey === ids[0]);
      return h?.practiceName || '';
    }
    return `${ids.length} hospitals selected`;
  }

  // ─── quarter / goal ───────────────────────────────────────────────────────
  // goalList entries are normalized to: { key, goalName, covered, total, isPartial, missingHospitalIds }
  // `key` is what's bound to the <select>, and it means different things per save path, because
  // these are two separate endpoints with two different shapes:
  //   - getgoalsfortask  (single hospital) → flat rows with that hospital's concrete goalTargetId,
  //     which is what a normal task stores in affiliatedGoalId.
  //   - getgoalcoverages (multi hospital)  → GoalCoverageDto rows keyed by goalDefinitionId, since
  //     one goal spanning several hospitals has a different goalTargetId per hospital.
  //     bulkcreatetask-new takes that goalDefinitionId and re-resolves each hospital's target
  //     server-side, so the definition id — not goalTargetId — must be what gets saved here.
  private mapSingleHospitalGoals(raw: any[]): any[] {
    return raw.map((g: any) => ({
      key: g.goalTargetId, goalName: g.goalName,
      covered: 1, total: 1, isPartial: false, missingHospitalIds: []
    })).filter((g: any) => g.key != null);
  }

  private tryLoadGoals() {
    const ids = this.selectedHospitalIds;
    const quarter = this.taskForm.get('quarter')!.value;
    if (this.goalQuarterDisabled || ids.length === 0 || !quarter) {
      this.goalList = [];
      return;
    }

    if (ids.length === 1) {
      this.leaderService.getGoalDictionaryWithQtr(ids[0], quarter.quarterName).subscribe((res: any) => {
        this.goalList = this.mapSingleHospitalGoals(res?.data || []);
        this.applyGoalPrefill();
        if (this.goalList.length === 0) {
          this.leaderService.showNotification('No goals found for the selected hospital and quarter.', true);
        }
      });
      return;
    }

    // Multi-hospital: union of goals across the selected hospitals, with coverage counts.
    this.leaderService.getGoalsForTaskBulk(ids, quarter.quarterName).subscribe({
      next: (res: any) => {
        const raw = res?.data || [];
        this.goalList = raw.map((g: any) => {
          const instances = g.instances || [];
          // usp_GetGoalInstancesForPracticeKeys INNER JOINs GoalHospitalTarget, so a hospital
          // without the goal has no row at all — it never appears in `instances`, and its
          // goalTargetId is never null. Derive the gap from the selected ids instead.
          const coveredHospitalIds = new Set<number>(
            instances.filter((i: any) => i.goalTargetId != null).map((i: any) => i.practiceSKey)
          );
          const covered = coveredHospitalIds.size;
          const total = g.hospitalsTotal ?? ids.length;
          const missingHospitalIds = ids.filter((id: any) => !coveredHospitalIds.has(id));
          return {
            // key must stay goalDefinitionId: bulkcreatetask-new matches on it and resolves each
            // hospital's own goalTargetId from it, so it has to identify the goal across all the
            // selected hospitals, not any single hospital's target.
            key: g.goalDefinitionId, goalName: g.goalName,
            covered, total, isPartial: covered < total, missingHospitalIds
          };
        });
        if (this.goalList.length === 0) {
          this.leaderService.showNotification('No goals found for the selected hospitals and quarter.', true);
        }
      },
      error: (err) => {
        console.error('Failed to load multi-hospital goals', err);
        this.goalList = [];
      }
    });
  }

  get selectedGoalInfo(): any {
    const key = this.taskForm.get('affiliatedGoal')?.value;
    if (!key) return null;
    return this.goalList.find(g => g.key === key) || null;
  }

  get skippedHospitalNames(): string[] {
    const g = this.selectedGoalInfo;
    if (!g?.isPartial) return [];
    return (g.missingHospitalIds || [])
      .map((id: number) => this.hospitalList.find(h => h.practiceSKey === id)?.practiceName)
      .filter(Boolean);
  }

  // ─── recurring ────────────────────────────────────────────────────────────

  get isRecurring(): boolean {
    const val = this.taskForm.get('recurringInterval')?.value;
    return !!val && val !== 'One-time';
  }

  onRecurringChange() {
    if (this.isRecurring) {
      this.taskForm.get('quarter')!.setValue(null);
      this.taskForm.get('affiliatedGoal')!.setValue(0);
      this.goalList = [];
      this.taskForm.get('endDate')?.setValidators([Validators.required]);
      this.taskForm.get('dueDate')?.setValue(new Date());

      // Set minEndDate based on interval
      const interval = this.taskForm.get('recurringInterval')?.value;
      const min = new Date();
      if (interval === 'Monthly') {
        min.setMonth(min.getMonth() + 1);
      } else if (interval === 'Quarterly') {
        min.setMonth(min.getMonth() + 3);
      } else if (interval === 'Annually') {
        min.setFullYear(min.getFullYear() + 1);
      }
      this.minEndDate = min;

      // Reset endDate if current value is before new minEndDate
      const currentEnd = this.taskForm.get('endDate')?.value;
      if (currentEnd && new Date(currentEnd) < min) {
        this.taskForm.get('endDate')?.setValue(null);
      }
    } else {
      this.taskForm.get('endDate')?.clearValidators();
      this.upcomingOccurrences = [];
    }
    this.taskForm.get('endDate')?.updateValueAndValidity();
  }

  generateOccurrences() {
    let startDate = this.taskForm.get('dueDate')?.value;
    const endDate = this.taskForm.get('endDate')?.value;
    const interval = this.taskForm.get('recurringInterval')?.value;
    const dueDays = this.taskForm.get('dueDaysFromStartDate')?.value || 0;

    if (this.isRecurring && !startDate) {
      startDate = new Date();
    }

    if (!startDate || !endDate || !this.isRecurring) {
      this.upcomingOccurrences = [];
      return;
    }

    const occurrences = [];
    let currentStart = new Date(startDate);
    const end = new Date(endDate);

    // Safety check to avoid infinite loop
    let iterations = 0;
    while (currentStart <= end && iterations < 100) {
      iterations++;
      const occurrenceStart = new Date(currentStart);
      const occurrenceDue = new Date(currentStart);
      occurrenceDue.setDate(occurrenceDue.getDate() + dueDays);

      occurrences.push({
        occurrenceDate: occurrenceStart,
        calculatedDueDate: occurrenceDue
      });

      if (interval === 'Monthly') {
        currentStart.setMonth(currentStart.getMonth() + 1);
      } else if (interval === 'Quarterly') {
        currentStart.setMonth(currentStart.getMonth() + 3);
      } else if (interval === 'Annually') {
        currentStart.setFullYear(currentStart.getFullYear() + 1);
      } else {
        break;
      }
    }
    this.upcomingOccurrences = occurrences;
  }

  // ─── assignee search ──────────────────────────────────────────────────────

  onAssigneeInput(event: any) {
    const q = event.target.value;
    this.assigneeQuery = q;
    if (this.isEditMode) {
      // Edit task: unchanged from before — typing clears the current selection until re-picked.
      this.selectedAssignees = [];
      this.taskForm.get('attendeesIds')!.setValue([]);
    }
    clearTimeout(this.assigneeTimer);
    if (q.length < 2) { this.assigneeSuggestions = []; this.showAssigneeDrop = false; return; }
    this.assigneeTimer = setTimeout(() => {
      this.leaderService.searchUserDataList(q).subscribe((res: any) => {
        const all = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.assigneeSuggestions = all.filter((u: any) => (u?.email || '').toLowerCase());
        this.showAssigneeDrop = this.assigneeSuggestions.length > 0;
      });
    }, 300);
  }

  /** Edit task: unchanged — single assignee, shown in the search box itself.
   *  New task, multi-hospital: adds to the chip list (dedupe by id).
   *  New task, single hospital: still one-at-a-time (replaces). */
  selectAssignee(user: any) {
    if (this.isBulkMode) {
      if (!this.selectedAssignees.some(a => a.id === user.id)) {
        this.selectedAssignees = [...this.selectedAssignees, user];
      }
    } else {
      this.selectedAssignees = [user];
    }
    this.taskForm.get('attendeesIds')!.setValue(this.selectedAssignees.map(a => a.id));
    this.assigneeQuery = this.isEditMode ? user.displayName : '';
    this.assigneeSuggestions = [];
    this.showAssigneeDrop = false;
  }

  removeAssignee(user: any) {
    this.selectedAssignees = this.selectedAssignees.filter(a => a.id !== user.id);
    this.taskForm.get('attendeesIds')!.setValue(this.selectedAssignees.map(a => a.id));
  }

  /** Edit task only — restores the pre-bulk "clear selection" button next to the search box. */
  clearAssignee() {
    this.selectedAssignees = [];
    this.assigneeQuery = '';
    this.taskForm.get('attendeesIds')!.setValue([]);
    this.assigneeSuggestions = [];
    this.showAssigneeDrop = false;
  }

  get isCurrentUserAssigned(): boolean {
    return !!this.currentUser && this.selectedAssignees.some(a => a.id === this.currentUser.userId);
  }

  assignToMe() {
    if (!this.currentUser) return;
    const me = { id: this.currentUser.userId, displayName: this.currentUser.displayName, email: this.currentUser.email };
    this.selectAssignee(me);
  }

  // ─── assign-to-all (RM/DO/VP) + preview ─────────────────────────────────────
  // roleOptions/the form control use the display label ('RM'); the backend's role code for
  // RM is 'LTKRM' (see user-management.component.ts) — convert at the API boundary only.

  private toApiRole(role: string): string {
    return role === 'RM' ? 'LTKRM' : role;
  }

  isRoleChecked(role: string): boolean {
    return (this.taskForm.get('assignToRoles')?.value || []).includes(role);
  }

  get assigneeRequired(): boolean {
    return !(this.isBulkMode && (this.taskForm.get('assignToRoles')?.value || []).length > 0);
  }

  toggleRole(role: string) {
    const ctrl = this.taskForm.get('assignToRoles')!;
    const current: string[] = ctrl.value || [];
    ctrl.setValue(current.includes(role) ? current.filter(r => r !== role) : [...current, role]);
    this.refreshRoleAssignees();
    this.updateAssigneeValidity();
  }

  /** A typed assignee is required unless in bulk mode + at least one role is checked (roles resolve to people server-side). */
  private updateAssigneeValidity() {
    const ctrl = this.taskForm.get('attendeesIds');
    if (!ctrl) return;
    const roles: string[] = this.taskForm.get('assignToRoles')?.value || [];
    if (this.isBulkMode && roles.length > 0) {
      ctrl.clearValidators();
    } else {
      ctrl.setValidators([Validators.required]);
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private refreshRoleAssignees() {
    const roles: string[] = this.taskForm.get('assignToRoles')?.value || [];
    const ids = this.selectedHospitalIds;
    if (!this.isBulkMode || roles.length === 0 || ids.length === 0) {
      this.roleAssignees = [];
      return;
    }
    this.roleAssigneesLoading = true;
    this.leaderService.getRoleAssignees(ids, roles.map(r => this.toApiRole(r))).subscribe({
      next: (res: any) => {
        this.roleAssignees = res?.data || [];
        this.roleAssigneesLoading = false;
      },
      error: (err) => {
        console.error('Failed to resolve role assignees', err);
        this.roleAssignees = [];
        this.roleAssigneesLoading = false;
      }
    });
  }

  /** Hospitals that will actually get a task — excludes any dropped for missing the selected (partial) goal. */
  get activeHospitals(): any[] {
    if (!this.isBulkMode) return [];
    const skipped = new Set(this.selectedGoalInfo?.isPartial ? this.selectedGoalInfo.missingHospitalIds : []);
    return this.hospitalList.filter(h => this.selectedHospitalIds.includes(h.practiceSKey) && !skipped.has(h.practiceSKey));
  }

  get skippedHospitals(): any[] {
    if (!this.isBulkMode) return [];
    const skipped = new Set(this.selectedGoalInfo?.isPartial ? this.selectedGoalInfo.missingHospitalIds : []);
    return this.hospitalList.filter(h => skipped.has(h.practiceSKey));
  }

  /** Per-hospital preview of who gets a task: typed assignees (apply everywhere) ∪ role-resolved people (deduped). */
  get assigneePreview(): { hospital: any; people: any[]; missingRoles: string[] }[] {
    if (!this.isBulkMode) return [];
    const roles: string[] = this.taskForm.get('assignToRoles')?.value || [];

    return this.activeHospitals.map(h => {
      const people = new Map<any, any>();
      this.selectedAssignees.forEach(a => people.set(a.id, a));
      const missingRoles: string[] = [];
      roles.forEach(role => {
        const apiRole = this.toApiRole(role);
        const match = this.roleAssignees.find((r: any) => r.practiceSKey === h.practiceSKey && r.role === apiRole);
        if (match?.assignee) {
          people.set(match.assignee.id, { ...match.assignee, role });
        } else {
          missingRoles.push(role);
        }
      });
      return { hospital: h, people: Array.from(people.values()), missingRoles };
    });
  }

  get totalTaskCount(): number {
    if (!this.isBulkMode) return 1;
    return this.assigneePreview.reduce((sum, p) => sum + p.people.length, 0);
  }

  /** Prefills the goal picker when the dialog was opened from a specific goal (e.g. "Add task" on a goal card). Single-hospital only — that entry point always passes exactly one hospital. */
  private applyGoalPrefill() {
    // Edit mode patches 'affiliatedGoal' itself (from the task row's own affiliatedGoalId) —
    // stay out of its way so this generic prefill can't race/clobber that value.
    if (this.isEditMode) return;

    const goalControl = this.taskForm?.get('affiliatedGoal');
    if (!goalControl) {
      return;
    }

    const selectedGoalValue = goalControl.value;
    if (selectedGoalValue && selectedGoalValue !== 0) {
      return;
    }

    const goalFromDialog = this.dialogData?.goal;
    if (!goalFromDialog || !this.goalList?.length) {
      goalControl.setValue(0);
      return;
    }

    const goalTargetId = goalFromDialog?.goalTargetId;
    const goalName = (goalFromDialog?.goalName ?? '').toString().trim().toLowerCase();

    const matchedGoal = this.goalList.find((goal: any) => {
      const listGoalName = (goal?.goalName ?? '').toString().trim().toLowerCase();
      return (goalTargetId && goal?.key === goalTargetId) || (!!goalName && listGoalName === goalName);
    });

    goalControl.setValue(matchedGoal?.key ?? 0);
  }

  getUserList(hospitalId?: any) {
    this.leaderService.getAssignedUserDictionary(hospitalId).subscribe((res: any) => {
      this.userList = res.data || [];
    });
  }

  // ─── dialog prefill ───────────────────────────────────────────────────────

 
  private applyDialogPrefill() {
    if (!this.dialogData) {
      return;
    }

    // ── Edit-mode: task row passed directly ──────────────────────────────────
    if (this.dialogData?.task) {
      this.isEditMode = true;
      const task = this.dialogData.task;
      this.taskId = task.taskId;

      // Basic text/select fields
      this.taskForm.patchValue({
        task:            task.task || task.taskName || null,
        category:        task.categoryId ?? null,
        taskDescription: richTextToHtml(task.taskDescription) ?? '',
        status:          task.statusId ?? 11,
        recurringInterval: task.recurringInterval || 'One-time',
        endDate:         task.endDate ? new Date(task.endDate) : null,
        dueDaysFromStartDate: task.dueDaysFromStartDate || 0
      });

      // Due date
      if (task.dueDate) {
        this.taskForm.get('dueDate')?.setValue(new Date(task.dueDate));
      }

      // Assignee — set both the form value and the typeahead display
      if (task.assignedToId != null) {
        const assignedUser = {
          id: task.assignedToId,
          displayName: task.assignedTo || task.assignedName || '',
          email: task.assignedEmail || ''
        };
        this.selectAssignee(assignedUser);
      }

      // Hospital — patch hospitalIds (array control)
      const hospitalIdsControl = this.taskForm.get('hospitalIds');
      if (hospitalIdsControl && task.hospitalId) {
        if (!hospitalIdsControl.value?.length) {
          hospitalIdsControl.setValue([task.hospitalId]);
        }
      }

      // Quarter — patch after hospitalIds so goal loading can fire
      const quarterControl = this.taskForm.get('quarter');
      if (quarterControl && !quarterControl.value && this.quarterList?.length && task.quarterId) {
        const matchedQtr = this.quarterList.find((q: any) => q.quarterId === task.quarterId);
        if (matchedQtr) {
          quarterControl.setValue(matchedQtr);
        }
      }

      // Affiliated goal — load goal list first, then patch
      if (task.hospitalId && task.quarterId) {
        const qtr = this.quarterList.find((q: any) => q.quarterId === task.quarterId);
        if (qtr) {
          this.leaderService.getGoalDictionaryWithQtr(task.hospitalId, qtr.quarterName).subscribe((res: any) => {
            const raw = res?.data || [];
            this.goalList = this.mapSingleHospitalGoals(raw);
            // task.affiliatedGoalId is a concrete goalTargetId, which is exactly what each option
            // is bound to here, so the two ids are directly comparable.
            const matchedGoal = this.goalList.find((g: any) => g.key === task.affiliatedGoalId);
            this.taskForm.get('affiliatedGoal')?.setValue(matchedGoal?.key ?? 0);
          });
        }
      } else {
        this.taskForm.get('affiliatedGoal')?.setValue(task.affiliatedGoalId ?? 0);
      }

      return; // done — skip the new-task hospital/quarter prefill below
    }

    // ── New-task: hospital/quarter passed from dialog context (e.g. dashboard) ─
    const hospitalFromDialog = this.dialogData?.hospital;
    const quarterFromDialog  = this.dialogData?.quarter;

    const hospitalIdsControl = this.taskForm?.get('hospitalIds');
    const quarterControl     = this.taskForm?.get('quarter');

    if (hospitalIdsControl && !hospitalIdsControl.value?.length && this.hospitalList?.length) {
      const key  = hospitalFromDialog?.practiceSKey ?? hospitalFromDialog?.hospitalPracticeSKey;
      const name = (
        hospitalFromDialog?.hospitalName ??
        hospitalFromDialog?.practiceName ??
        hospitalFromDialog?.displayName ?? ''
      ).toString().trim().toLowerCase();

      const match = this.hospitalList.find(h => {
        const hKey  = h.practiceSKey ?? h.hospitalPracticeSKey;
        const hName = (h.practiceName ?? h.hospitalName ?? h.displayName ?? '').toString().trim().toLowerCase();
        return (key && hKey === key) || (name && hName === name);
      });

      if (match) {
        hospitalIdsControl.setValue([match.practiceSKey]);
      }
    }

    if (quarterControl && !quarterControl.value && this.quarterList?.length) {
      const qName = (quarterFromDialog?.quarterName ?? '').toString().trim().toLowerCase();
      const match = this.quarterList.find((q: any) =>
        (quarterFromDialog?.quarterId && q.quarterId === quarterFromDialog.quarterId) ||
        (qName && q.quarterName.toString().trim().toLowerCase() === qName)
      );
      if (match) {
        quarterControl.setValue(match);
      }
    }
  }



  // ─── submit ───────────────────────────────────────────────────────────────


  onSubmit() {
    if (this.taskForm) {
      let val = this.taskForm.value;
      if (!this.taskForm.valid) {
        this.taskForm.markAllAsTouched();
        this.leaderService.showNotification('Fill out all required fields.', true);
        return;
      }
      this.loading = true;
      const now = new Date().toISOString();
      let dueDate = this.taskForm.value.dueDate;
      let formattedDueDate = null;

      // If recurring, calculate the due date for the first occurrence
      if (this.isRecurring && this.upcomingOccurrences.length > 0) {
        dueDate = this.upcomingOccurrences[0].calculatedDueDate;
      }

      if (dueDate) {
        const d = new Date(dueDate);
        formattedDueDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
      }

      const assigneeIds: any[] = this.selectedAssignees.map(a => a.id);
      const userId: any = assigneeIds[0] ?? '';   // single-task endpoints only take one

      const hospitalIds: number[] = val.hospitalIds?.length ? val.hospitalIds : [];
      const hospitalId = hospitalIds.length === 1 ? hospitalIds[0] : null;
      const isMulti = hospitalIds.length > 1;

      let action;

      if (isMulti && !this.isEditMode) {
        // New bulk-create endpoint — one task per hospital × resolved assignee.
        // No taskId/recurrence fields: bulk create only, and recurrence × bulk is deferred.
        const bulkPayload = {
          practiceSKeys: hospitalIds,
          // GoalDefinitionId is a non-nullable int server-side — null fails deserialization (400).
          // 0 means "no goal", same as the form's default/"None" option value.
          goalDefinitionId: this.taskForm.value.affiliatedGoal || 0,
          assigneeIds,
          assignToRoles: (this.taskForm.value.assignToRoles || []).map((r: string) => this.toApiRole(r)),
          taskName: this.taskForm.value.task,
          taskDescription: richTextToHtml(this.taskForm.value.taskDescription),
          dueDate: formattedDueDate,
          statusId: this.taskForm.value.status || 11,
          categoryId: this.taskForm.value.category,
          quarterName: this.taskForm.value.quarter?.quarterName ?? null,
          isCritical: !!this.taskForm.value.isCritical
        };
        action = this.leaderService.bulkCreateTaskNew(bulkPayload);
      } else {
        const payload: any = {
          taskId: this.taskId,
          hospitalId,
          hospitalIds,
          taskName: this.taskForm.value.task,
          assignedToId: userId,
          taskDescription: richTextToHtml(this.taskForm.value.taskDescription),
          affiliatedGoalId: this.taskForm.value.affiliatedGoal || null,
          categoryId: this.taskForm.value.category,
          dueDate: formattedDueDate,
          statusId: this.taskForm.value.status || 11,
          quarterId: this.taskForm?.value.quarter?.quarterId ?? null,
          recurringInterval: this.taskForm.value.recurringInterval || 'One-time',
          endDate: this.taskForm.value.endDate,
          dueDaysFromStartDate: this.taskForm.value.dueDaysFromStartDate || 0,
          occurrences: this.upcomingOccurrences
        };
        action = this.isEditMode ? this.leaderService.updateTask(payload) : this.leaderService.cretateTask(payload);
      }

      action.subscribe({
        next: (res: any) => {
          this.loading = false; // Stop loading

          if (res.resultDetails && res.resultDetails[0]?.status === false) {
            this.leaderService.showNotification(`Error ${this.isEditMode ? 'updating' : 'creating'} task. Please try again.`, true);
            return;
          }
          this.leaderService.showNotification(`Task ${this.isEditMode ? 'updated' : 'created'} successfully!`, false);
          this.leaderService.triggerNotificationEvent('notification');
          this.leaderService.clearTaskListCache();
          this.dialogRef.close('success');
          this.taskForm.reset({ taskDescription: '' });
          this.leaderService.triggerOpsEventUpdate(true);
        },
        error: (err) => {
          this.loading = false; // Stop loading on error
          console.error(err);
          this.leaderService.showNotification(`An error occurred while ${this.isEditMode ? 'updating' : 'creating'} the task.`, true);
        }
      });
    } else {
      this.taskForm.markAllAsTouched();
      this.leaderService.showNotification('Fill out all required fields.', true);
    }
}

  closeModal() {
    this.formSubmitted = false;
    this.assigneeQuery = '';
    this.selectedAssignees = [];
    this.assigneeSuggestions = [];
    this.showAssigneeDrop = false;
    this.dialogRef.close('closed');
    this.taskForm.reset({ taskDescription: '' });
  }
}
